import React, { useState, useEffect } from 'react';
import { Snackbar, Button, Typography, Box } from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';

/**
 * SWUpdateNotifier — listens for the 'happyears-sw-update' custom event dispatched
 * by serviceWorkerRegistration.js when a new service worker version is waiting.
 *
 * Shows a bottom snackbar giving the user the choice to reload and activate the update.
 */
const SWUpdateNotifier = () => {
    const [waitingWorker, setWaitingWorker] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const handler = (e) => {
            const registration = e?.detail?.registration;
            if (registration?.waiting) {
                setWaitingWorker(registration.waiting);
                setOpen(true);
            }
        };

        window.addEventListener('happyears-sw-update', handler);
        return () => window.removeEventListener('happyears-sw-update', handler);
    }, []);

    const handleUpdate = () => {
        if (waitingWorker) {
            waitingWorker.postMessage({ type: 'SKIP_WAITING' });
        }
        setOpen(false);
        // Reload once the new SW takes control
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            window.location.reload();
        }, { once: true });
    };

    return (
        <Snackbar
            open={open}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            sx={{ mb: { xs: 8, sm: 2 }, ml: { sm: 1 } }}
            message={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SystemUpdateAltIcon fontSize="small" />
                    <Typography variant="body2">A new version of Happy Ears is available.</Typography>
                </Box>
            }
            action={
                <Button
                    color="primary"
                    size="small"
                    variant="contained"
                    disableElevation
                    onClick={handleUpdate}
                    sx={{ textTransform: 'none', fontSize: '0.78rem', mr: 1 }}
                >
                    Update now
                </Button>
            }
        />
    );
};

export default SWUpdateNotifier;
